import React, { useState } from 'react';
import { HeartIcon, AlertTriangleIcon, InfoIcon, CheckIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRomanceTagging } from '../../../hooks/useRomanceTagging';
import { SearchWithAutocomplete, Suggestion } from '../../../components/UI/SearchWithAutocomplete';
import { PAIRING_TYPES, ROMANCE_TROPES, CHARACTER_PAIRINGS, CONTENT_WARNINGS, DARKNESS_LEVELS } from '../../../data/romanceTaggingData';
// Build suggestion lists
const pairingTypeSuggestions: Suggestion[] = PAIRING_TYPES.map(pt => ({
  id: pt.toLowerCase().replace(/[\s\/]+/g, '-'),
  label: pt,
  type: 'Pairing'
}));
const tropeSuggestions: Suggestion[] = ROMANCE_TROPES.map(t => ({
  id: t.name.toLowerCase().replace(/\s+/g, '-'),
  label: t.name,
  type: t.isFlagged ? '⚠ Flagged' : 'Trope'
}));
const characterPairingSuggestions: Suggestion[] = CHARACTER_PAIRINGS.map(p => ({
  id: p.name.toLowerCase().replace(/[\s\/]+/g, '-'),
  label: p.name,
  type: 'Pairing'
}));
const warningBaseSuggestions: Suggestion[] = CONTENT_WARNINGS.map(w => ({
  id: w.name.toLowerCase().replace(/\s+/g, '-'),
  label: w.name,
  type: 'Warning'
}));
export const RomanceTaggingSection: React.FC = () => {
  const dk = useDarkClasses();
  const {
    selectedPairingType,
    setSelectedPairingType,
    selectedTropes,
    setSelectedTropes,
    selectedCharacterPairings,
    setSelectedCharacterPairings,
    selectedContentWarnings,
    setSelectedContentWarnings,
    darknessLevel,
    setDarknessLevel,
    warningsConfirmed,
    setWarningsConfirmed,
    classification,
    showDarknessPrompt
  } = useRomanceTagging();
  const [pairingSearch, setPairingSearch] = useState('');
  const [tropeSearch, setTropeSearch] = useState('');
  const [charPairingSearch, setCharPairingSearch] = useState('');
  const [warningSearch, setWarningSearch] = useState('');
  const hasFlaggedTrope = selectedTropes.some(t => ROMANCE_TROPES.find(def => def.name === t)?.isFlagged);
  const ACCENT = 'bg-pink-100 text-pink-700 border-pink-500 dark:bg-pink-900/30 dark:text-pink-300';
  const WARNING_ACCENT = 'bg-red-100 text-red-700 border-red-500 dark:bg-red-900/30 dark:text-red-300';
  return <div className={`p-6 rounded-xl shadow-sm border ${dk.border} ${dk.card} mt-6`} data-id="element-21050" data-mp="4ahykxgqqbrr">
      <h2 className={`text-lg font-bold mb-6 flex items-center ${dk.textHeading}`} data-id="element-21051" data-mp="94vxh9um3116">
        <HeartIcon className="w-5 h-5 mr-2 text-pink-500" data-id="element-21052" data-mp="d70jpniqcvds" />
        Romance Tagging System
      </h2>

      <div className="space-y-8" data-id="element-21053" data-mp="n2u7y45tadbe">
        {/* 1. Pairing Type */}
        <div data-id="element-21054" data-mp="n3dhoxlb5vec">
          <label className={`flex items-center text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21055" data-mp="n0r68mvaqvqu">
            Pairing Type{' '}
            <span className="ml-2 text-xs text-pink-500 font-semibold px-2 py-0.5 bg-pink-50 dark:bg-pink-900/20 rounded-full" data-id="element-21056" data-mp="2ouyx57vxewif">
              Required
            </span>
          </label>
          {selectedPairingType ? <div className="flex items-center gap-2 mt-1" data-id="element-21057" data-mp="1ayyuumeonspb">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${ACCENT}`} data-id="element-21058" data-mp="166xd4kyqx9rw">
                {selectedPairingType}
                <button onClick={() => setSelectedPairingType('')} className="ml-1.5 hover:opacity-75" data-id="element-21059" data-mp="x6r5vgtn5616">
                  <XIcon className="w-3 h-3" data-id="element-21060" data-mp="1twhyfaidgj4c" />
                </button>
              </span>
            </div> : <SearchWithAutocomplete value={pairingSearch} onChange={setPairingSearch} suggestions={pairingTypeSuggestions} placeholder="e.g. M/F, Why Choose, Reverse Harem..." onSelect={item => {
          setSelectedPairingType(item.label);
          setPairingSearch('');
        }} data-id="element-21061" data-mp="1ptllo0fijh8b" />}
        </div>

        {/* 2. Romance Tropes */}
        <div data-id="element-21062" data-mp="1sej97cnqjysg">
          <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21063" data-mp="1sdzt81hnbkh2">
            Romance Tropes
          </label>
          <SearchWithAutocomplete value={tropeSearch} onChange={setTropeSearch} suggestions={tropeSuggestions.filter(s => !selectedTropes.includes(s.label))} placeholder="e.g. Enemies to Lovers, Second Chance, Fake Dating..." onSelect={item => {
          setSelectedTropes(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
          setTropeSearch('');
        }} data-id="element-21064" data-mp="1je3cqn5etrx3" />
          {selectedTropes.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-21065" data-mp="275s8kdhfwyis">
              {selectedTropes.map(trope => <span key={trope} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${ACCENT}`} data-id="element-21066" data-mp="qrsbrbdw5ozr">
                  {trope}
                  <button onClick={() => setSelectedTropes(prev => prev.filter(t => t !== trope))} className="ml-1.5 hover:opacity-75" data-id="element-21067" data-mp="j5wutiihvash">
                    <XIcon className="w-3 h-3" data-id="element-21068" data-mp="s4rxwlq8s4rk" />
                  </button>
                </span>)}
            </div>}
          {hasFlaggedTrope && <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 flex items-start" data-id="element-21069" data-mp="h9pr00bilew">
              <InfoIcon className="w-4 h-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-21070" data-mp="9flfz0x9vh6t" />
              <p className="text-xs text-amber-800 dark:text-amber-200" data-id="element-21071" data-mp="3hvn9r8rn1zzl">
                You've selected tropes that may include power imbalance
                dynamics. This book may be classified as AfterTide.
              </p>
            </div>}
        </div>

        {/* 3. Character Pairings */}
        <div data-id="element-21072" data-mp="1ls8qdzzxb2h8">
          <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21073" data-mp="1ls0hej6x8hbb">
            Character Pairings
          </label>
          <SearchWithAutocomplete value={charPairingSearch} onChange={setCharPairingSearch} suggestions={characterPairingSuggestions.filter(s => !selectedCharacterPairings.includes(s.label))} placeholder="e.g. Boss/Employee, Bully/Victim, Royalty/Commoner..." onSelect={item => {
          setSelectedCharacterPairings(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
          setCharPairingSearch('');
        }} data-id="element-21074" data-mp="1qluvn73rc430" />
          {selectedCharacterPairings.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-21075" data-mp="2n8zv266ch47h">
              {selectedCharacterPairings.map(pairing => <span key={pairing} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${ACCENT}`} data-id="element-21076" data-mp="bl4gfigdi9ov">
                  {pairing}
                  <button onClick={() => setSelectedCharacterPairings(prev => prev.filter(p => p !== pairing))} className="ml-1.5 hover:opacity-75" data-id="element-21077" data-mp="2s9kxggpjsca">
                    <XIcon className="w-3 h-3" data-id="element-21078" data-mp="3k4bzb5jdbudd" />
                  </button>
                </span>)}
            </div>}
        </div>

        {/* 4. Content Warnings */}
        <div data-id="element-21079" data-mp="3b3dsyglgjarm">
          <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21080" data-mp="3b3m3i1u3y3zn">
            Content Warnings
          </label>
          <SearchWithAutocomplete value={warningSearch} onChange={setWarningSearch} suggestions={warningBaseSuggestions.filter(s => !selectedContentWarnings.includes(s.label))} placeholder="e.g. Dubious Consent, Violence, Kidnapping..." onSelect={item => {
          setSelectedContentWarnings(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
          setWarningSearch('');
        }} data-id="element-21081" data-mp="19b7qnh6f7pq3" />
          {selectedContentWarnings.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-21082" data-mp="3gpfp8vrg2d8">
              {selectedContentWarnings.map(w => <span key={w} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${WARNING_ACCENT}`} data-id="element-21083" data-mp="3ln3tn7u14cbm">
                  {w}
                  <button onClick={() => setSelectedContentWarnings(prev => {
              const next = prev.filter(x => x !== w);
              if (next.length === 0) setWarningsConfirmed(false);
              return next;
            })} className="ml-1.5 hover:opacity-75" data-id="element-21084" data-mp="1js36ifs2h4g5">
                    <XIcon className="w-3 h-3" data-id="element-21085" data-mp="3eaeda6igrwru" />
                  </button>
                </span>)}
            </div>}

          {selectedContentWarnings.length > 0 && <div className={`mt-3 p-4 rounded-lg border ${dk.border} ${dk.surfaceMuted}`} data-id="element-21086" data-mp="3n2q0zkm71644">
              <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${dk.textMuted}`} data-id="element-21087" data-mp="5v4dx65yl5nr">
                Selected Warnings Definitions
              </h4>
              <ul className="space-y-2 mb-4" data-id="element-21088" data-mp="27lo52aajvns6">
                {selectedContentWarnings.map(w => {
              const def = CONTENT_WARNINGS.find(cw => cw.name === w)?.definition;
              return <li key={w} className="text-sm flex items-start" data-id="element-21089" data-mp="13mcmo4un7rkt">
                      <span className="font-semibold text-red-600 dark:text-red-400 mr-2" data-id="element-21090" data-mp="ykwwxzhisww3">
                        {w}:
                      </span>
                      <span className={dk.textSecondary} data-id="element-21091" data-mp="toon91m3dvwb">{def}</span>
                    </li>;
            })}
              </ul>

              <div className={`pt-3 border-t ${dk.border} flex items-center justify-between`} data-id="element-21092" data-mp="1mahjstgsoean">
                <p className={`text-sm ${dk.textPrimary}`} data-id="element-21093" data-mp="1hd7vxi920aox">
                  These will be visible to readers.
                </p>
                <button onClick={() => setWarningsConfirmed(!warningsConfirmed)} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${warningsConfirmed ? 'bg-green-100 text-green-700 border border-green-500 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-600 text-white hover:bg-red-700'}`} data-id="element-21094" data-mp="17mmp57j87zux">
                  {warningsConfirmed ? <>
                      <CheckIcon className="w-4 h-4 mr-1.5" data-id="element-21095" data-mp="2cm1ptytd3epd" /> Confirmed
                    </> : 'Confirm Warnings'}
                </button>
              </div>
            </div>}
        </div>

        {/* 5. Darkness Level */}
        <div data-id="element-21096" data-mp="2riaoay5fho0q">
          <div className="flex items-center justify-between mb-3" data-id="element-21097" data-mp="2rik5pxceqmut">
            <label className={`text-sm font-medium ${dk.textSecondary}`} data-id="element-21098" data-mp="2vjcekphdapqk">
              Darkness Level
            </label>
            {classification.darkness_required && <span className="text-xs text-red-500 font-semibold px-2 py-0.5 bg-red-50 dark:bg-red-900/20 rounded-full" data-id="element-21099" data-mp="35aqkbezwwls5">
                Required based on tags
              </span>}
          </div>

          {showDarknessPrompt && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800 flex items-start" data-id="element-21100" data-mp="3f8mt405dn4n3">
              <AlertTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-21101" data-mp="2kleiob0z2yf2" />
              <p className="text-sm text-red-800 dark:text-red-200 font-medium" data-id="element-21102" data-mp="2za2bi9jfou1b">
                Your selected tags suggest this book may contain darker themes.
                Please confirm the darkness level.
              </p>
            </div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-21103" data-mp="3895vdfqfddgv">
            {DARKNESS_LEVELS.map(({
            level,
            description
          }) => {
            const isSelected = darknessLevel === level;
            // Soft recommendation: if required but not selected, subtly highlight Medium
            const isSoftRecommended = showDarknessPrompt && level === 'Medium';
            return <button key={level} onClick={() => setDarknessLevel(level)} className={`p-3 rounded-xl border text-left transition-all
                    ${isSelected ? 'bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900' : isSoftRecommended ? `border-slate-400 border-dashed ${dk.surfaceMuted}` : `${dk.surfaceMuted} ${dk.border} hover:border-slate-400`}`} data-id="element-21104" data-mp="1nvf86b19hptu">
                  <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21105" data-mp="3prnutqqoe6n0">
                    {level}
                  </div>
                  <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21106" data-mp="3ftzwkqtv2h03">
                    {description}
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* 6. Classification Preview */}
        <div className={`mt-8 p-5 rounded-xl border-2 ${classification.aftertide_eligible ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-700' : `border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50`}`} data-id="element-21107" data-mp="102u94tqisz75">
          <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 ${dk.textMuted}`} data-id="element-21108" data-mp="2w68qxy2l7one">
            Classification Preview
          </h3>

          <div className="space-y-4" data-id="element-21109" data-mp="2s33sc6lmo741">
            {/* AfterTide Status */}
            <div data-id="element-21110" data-mp="2n4ips0iwlbcf">
              <div className="flex items-center mb-1" data-id="element-21111" data-mp="2n525rboztpnt">
                <span className={`font-semibold text-sm ${classification.aftertide_eligible ? 'text-amber-600 dark:text-amber-400' : dk.textPrimary}`} data-id="element-21112" data-mp="2i70peyiyc4g1">
                  {classification.aftertide_eligible ? 'AfterTide Classified' : 'Standard Catalog'}
                </span>
              </div>
              {classification.aftertide_reasons.length > 0 && <ul className="list-disc list-inside pl-4 mt-1" data-id="element-21113" data-mp="1zeof9pm23yvc">
                  {classification.aftertide_reasons.map((reason, i) => <li key={i} className={`text-xs ${dk.textSecondary}`} data-id="element-21114" data-mp="9yugots4nq04" data-mpi={i}>
                      {reason}
                    </li>)}
                </ul>}
            </div>

            {/* Auto-generated Dynamics */}
            <div data-id="element-21115" data-mp="jy34f4weg8e1">
              <span className={`block text-xs font-semibold mb-1 ${dk.textSecondary}`} data-id="element-21116" data-mp="jxjrk2llwa6v">
                Auto-Generated Dynamics:
              </span>
              {classification.relationship_dynamics.length > 0 ? <div className="flex flex-wrap gap-1.5" data-id="element-21117" data-mp="3scbt41vswf0x">
                  {classification.relationship_dynamics.map(d => <span key={d} className={`px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300`} data-id="element-21118" data-mp="898qfqkd94j">
                      {d}
                    </span>)}
                </div> : <span className={`text-xs italic ${dk.textMuted}`} data-id="element-21119" data-mp="38mrkarfpr6sw">None</span>}
            </div>

            {/* Overlay Preview */}
            <div className={`p-3 rounded-lg bg-white dark:bg-slate-900 border ${dk.border} shadow-sm`} data-id="element-21120" data-mp="aw2od0j6c916">
              <span className={`block text-xs font-semibold mb-2 ${dk.textSecondary}`} data-id="element-21121" data-mp="pjns8wnvica5">
                Card Overlay Preview:
              </span>
              <div className="flex flex-wrap items-center gap-2" data-id="element-21122" data-mp="3t1yt729omaa8">
                <span className={`font-bold text-sm ${dk.textPrimary}`} data-id="element-21123" data-mp="3o22p730ziiuo">
                  {selectedPairingType || 'Pairing'}
                </span>

                {classification.overlay_tropes.length > 0 && <>
                    <span className={dk.textMuted} data-id="element-21124" data-mp="1ttf0dzn6lpd">•</span>
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-21125" data-mp="1hb390a2r14sg">
                      {classification.overlay_tropes.join(' • ')}
                    </span>
                  </>}

                {classification.overlay_flags.showDynamic && classification.overlay_dynamics.length > 0 && <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300" data-id="element-21126" data-mp="1pteyvaaf9i4p">
                      {classification.overlay_dynamics[0]}
                    </span>}

                {classification.overlay_flags.showWarningBadge && <span className="px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-xs font-bold text-red-700 dark:text-red-400" data-id="element-21127" data-mp="23kabmxo1kjdk">
                    !
                  </span>}

                {classification.overlay_flags.showAfterTide && <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-xs font-bold text-amber-700 dark:text-amber-400" data-id="element-21128" data-mp="1ysa7su4up6bp">
                    AfterTide
                  </span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};